import {
  BackEndCodeLanguageType,
  BuildSystemType,
  DefaultTransformationEntityType,
  ExternalGitProviderType,
  ForkModeType,
  SyncModeType,
  VersionControlMode,
  VersionControlModeType
} from '@prophecy/graphql-types/enums';
import { graphql } from '@prophecy/graphql-types/graphql';
import { ReleaseFromAnExistingTagMutation } from '@prophecy/queries/common/queries/project';
import { gql } from 'graphql-request';

import { makeGraphQlCall } from '../../data/util';
import { HarmonizationStatus } from '../types';
import { getAspectValue } from '../utils/misc/aspect';
import { Aspect, DetailTypeWithProject, VersionedAspect } from './common';

export {
  projectDetailQuery,
  projectReleasesQuery,
  SqlProjectAboutQuery,
  ProjectAboutQuery,
  PackageLatestReleaseQuery,
  projectTemplatesQuery,
  ProjectGitStatusQuery,
  gitCodeQuery,
  subscriberTeamsQuery,
  packageSubscriberTeamsQuery,
  subscribedDatasetsQuery,
  createProjectWithExternalGitEntityMutationQuery,
  createProjectWithoutExternalGitEntityMutationQuery,
  unlinkExternalGitMutation,
  linkInternalGitMutation,
  linkExternalGitMutation,
  linkSavedCredsToExternalGitMutation,
  updateProject,
  addTemplateQuery,
  UpdateGitTokenMutation,
  cloneProjectMutation,
  migrateToExternalGitMutation,
  ClusterProjectCreationDefaultsQuery,
  UpdateClusterProjectCreationDefaultsMutation,
  ProjectDependenciesQuery,
  TeamProjectCreationDefaultsQuery,
  UpdateTeamProjectCreationDefaultsMutation,
  addOrUpdateCopilotLLMConfigMutation,
  getCopilotLLMConfigQuery,
  setNetworkProxyConfigMutation,
  getOrchestrationSettingsQuery,
  addOrchestrationEndpointMutation,
  updateOrchestrationEndpointMutation,
  removeOrchestrationEndpointMutation,
  removeProjectDependencyMutation,
  addDefaultDependenciesMutation,
  setAnalysisEnabledMutation,
  removeDefaultDependenciesMutation,
  addProjectDependencyMutation,
  updateProjectDependencyMutation,
  updateProjectDependenciesMutation,
  prCreationTemplateQuery,
  prCreationTemplateMutation,
  prCreationURLQuery,
  updateTemplateMutation,
  updateArtefactIDMutation,
  updateParentArtefactIDForProjectMutation,
  mergedExternallyMutation,
  externalProjectDetailsQuery,
  UpdateProjectReadmeMutation,
  ReleaseFromAnExistingTagMutation,
  addOrUpdateTeamProjectCreationTemplateMutation,
  teamProjectCreationTemplatesQuery,
  getProjectStatusConfigQuery,
  setProjectStatusConfigMutation,
  ListProjectFilesQuery
} from '@prophecy/queries/common/queries/project';
export type {
  ProjectDetailQuery,
  SqlProjectAboutQueryResponse,
  ProjectAboutQueryResponse,
  UserGitCreds,
  ClusterProjectCreationDefaultsQueryResponse,
  ProjectDependenciesQueryResponse,
  TeamProjectCreationDefaultsQueryResponse,
  GetOrchestrationSettingsQueryResponse,
  ProjectStatusConfigType
} from '@prophecy/queries/common/queries/project';

export type ProjectDetail = {
  _id: string;
  name: string;
  language: BackEndCodeLanguageType;
  providerType?: string;
  pipelinesCount: number;
  defaultTransformationEntity: DefaultTransformationEntityType;
  created: number;
  createdBy: string;
  mainBranch: string;
  forkMode: ForkModeType;
  syncMode: SyncModeType;
  versionControlMode: VersionControlModeType;
  team: { _id: string; name: string };
  latestAttemptedReleaseVersion?: string;
  publishedToPackageHub: boolean;
  clonedReleaseVersion?: string;
};
export type HarmonizationProperties = {
  status?: `${HarmonizationStatus}` | null;
  mappings: Array<{
    fileName: string;
    schema: string;
    tableProperties?: {
      catalog?: string | null;
      schema?: string | null;
      table?: string | null;
    } | null;
  }>;
};
export type ProjectHarmonizationPropertiesResponse = {
  Project?: { _id: string; harmonizationProperties?: HarmonizationProperties | null } | null;
};

export type Template = {
  _id: string;
  versionedAspects: VersionedAspect[];
};

export enum GitTagState {
  ExternalAndInternallyReleased = 'ExternalAndInternallyReleased',
  InternalReleased = 'InternalReleased',
  ExternalUnReleased = 'ExternalUnReleased'
}

export const projectHarmonizationPropertiesQuery = gql`
  query ProjectHarmonizationProperties($projectId: String!) {
    Project(uid: $projectId) {
      _id
      harmonizationProperties {
        status
        mappings {
          fileName
          schema
          tableProperties {
            catalog
            schema
            table
          }
        }
      }
    }
  }
`;

export const updateHarmonizationStatusMutation = gql`
  mutation UpdateHarmonizationStatus($uid: String!, $status: String!) {
    updateHarmonizationStatus(uid: $uid, status: $status)
  }
`;

type DeploymentSettings = {
  isAutoDeployEnabled: boolean;
  isSelectiveJobDeploymentEnabled: boolean;
  isUTEnabled: boolean;
};

export type ProjectSettings<T extends boolean> = {
  deploymentSettings?: DeploymentSettings;
} & (T extends false
  ? {
      artefactID: string;
      parentArtefactID: string;
      changelogFile: string;
      buildSystemTemplatesEnabled?: boolean;
      defaultTemplate: string;
      templates: Template[];
    }
  : T extends true
    ? {}
    : never);

//  FIXME: remove once gql schema types are working
export type PackageSubscriberTeamsQueryResponse = {
  ProjectReleaseSnapshot: {
    subscriberTeamsValidForUser: { _id: string }[];
  };
};

export type ProjectType = DetailTypeWithProject & {
  _id: string;
  name: string;
  mainBranch: string;
  mainBranchModificationAllowed: boolean;
  language: BackEndCodeLanguageType;
  providerType?: string;
  versionControlMode?: VersionControlModeType;
};

export type UserProjectsQueryType = {
  User: {
    projects: ProjectType[];
  };
};

export type ProjectEntityVariables = {
  name: string;
  ownerUID: string;
  mainBranch: string;
  forkMode: ForkModeType;
  language: BackEndCodeLanguageType;
  backendLanguage?: BackEndCodeLanguageType;
  dependencyProjectUIDs?: string[];
  externalUriArg?: string;
  externalSubPath?: string;
  externalRepoEmail?: string;
  externalRepoUsername?: string;
  versionControlMode: VersionControlModeType;
  externalRepoAccessToken?: string;
  externalGitProvider?: ExternalGitProviderType;
  userGitCredsUID?: number;
  isImport?: boolean;
  defaultDevelopmentBranch?: string;
  defaultTransformationEntity?: DefaultTransformationEntityType;
  isHarmonizationAnalysis?: boolean;
};

export const createHarmonizationProjectMutation = graphql(`
  mutation createHarmonizationProject(
    $name: String!
    $ownerUID: String!
    $mainBranch: String
    $language: BackEndCodeLanguage
    $forkMode: ForkMode!
    $dependencyProjectUIDs: [String!]
    $description: String
    $providerType: String
    $versionControlMode: VersionControlMode
    $isImport: Boolean
    $defaultDevelopmentBranch: String
    $defaultTransformationEntity: DefaultTransformationEntity
    $isOnboardingEntity: Boolean
    $harmonizationProperties: HarmonizationPropertiesInput!
  ) {
    addProject(
      name: $name
      ownerUID: $ownerUID
      mainBranch: $mainBranch
      language: $language
      forkMode: $forkMode
      dependencyProjectUIDs: $dependencyProjectUIDs
      description: $description
      providerType: $providerType
      versionControlMode: $versionControlMode
      isImport: $isImport
      defaultDevelopmentBranch: $defaultDevelopmentBranch
      defaultTransformationEntity: $defaultTransformationEntity
      isOnboardingEntity: $isOnboardingEntity
      harmonizationProperties: $harmonizationProperties
    ) {
      _id
      name
    }
  }
`);

export type CloneProjectVariable = {
  uid: string;
  name: string;
  teamUID: string;
  externalSubPath?: string;
  externalUriArg?: string;
  externalRepoUsername?: string;
  externalRepoEmail?: string;
  externalRepoAccessToken?: string;
  copyMainBranchReleaseTags: boolean;
  commitTemplate?: string;
  userGitCredsUID?: number;
  externalGitProvider?: ExternalGitProviderType;
  versionControlMode?: VersionControlMode;
  defaultDevelopmentBranch?: string;
};

export type ExternalGitCloneProjectVariable = {
  uid: string;
  name: string;
  teamUID: string;
  externalSubPath?: string;
  externalUriArg?: string;
  externalRepoUsername?: string;
  externalRepoEmail?: string;
  externalRepoAccessToken?: string;
  copyMainBranchReleaseTags: boolean;
  commitTemplate?: string;
  userGitCredsUID?: number;
  externalGitProvider?: ExternalGitProviderType;
  versionControlMode?: VersionControlMode;
  defaultDevelopmentBranch?: string;
  defaultMainBranch?: string;
};

export type CloneProjectResponse = {
  cloneProject: {
    _id: string;
    name: string;
  };
};

export type MigrateToExternalGitResponse = {
  migrateToExternalGit: {
    _id: string;
    name: string;
  };
};

export type DependencyConflictType = {
  conflictingReleaseVersionsSummary: string[];
  project: {
    name: string;
  };
};

export type ExternalProjectDetails = {
  isValidProphecyProject: boolean;
  isValidDbtProject: boolean;
  name: string;
  description: string;
  language: BackEndCodeLanguageType;
  buildSystem: BuildSystemType;
  versionControlMode: VersionControlModeType;
  isSubpathCreationAllowedSimple: boolean;
  existingProjectsMessage: string;
  backendLanguage: BackEndCodeLanguageType;
};

export const fetchProjectSettings =
  <T extends boolean>(onlyDeploymentSettings: T) =>
  async (variables: { projectId: string; branch?: string }) => {
    const advancedSettingsQuery = `
      changelogFile
      buildSystemTemplatesEnabled
      defaultTemplate
      artefactID
      parentArtefactID
      templates {
        _id
        versionedAspects(branch: $branch, aspectVers: [{ aspect: Configuration }]) {
          VersionedAspectName
          VersionedAspectValue
        }
      }
    `;

    const query = gql`
      query ProjectAdvanceTabQuery($projectId: String!${onlyDeploymentSettings ? '' : `, $branch: String`}) {
        Project(uid: $projectId) {
          ${onlyDeploymentSettings ? '' : advancedSettingsQuery}
          DeploymentSettingsAspect: aspects(aspect: DeploymentSettings) {
            AspectValue
            AspectName
          }
        }
      }
    `;

    const response = (await makeGraphQlCall(query, variables)) as {
      Project: {
        DeploymentSettingsAspect: Aspect<DeploymentSettings>[];
      };
    };

    const {
      Project: { DeploymentSettingsAspect, ...output }
    } = response;

    const deploymentSettings = getAspectValue(DeploymentSettingsAspect);

    return { ...output, deploymentSettings } as ProjectSettings<T>;
  };

export const ProjectDropdownOptionsQuery = (ownerEntity: string, childEntity: string, aspectQuery: string) => gql`
  query ProjectDropdownOptionsQuery($entityId: String!) {
    ${ownerEntity}(uid: $entityId) {
      _id
      ${childEntity} {
        _id
        name
        mainBranch
        mainBranchModificationAllowed
        language
        versionControlMode
        ${aspectQuery}
      }
    }
  }
`;

export const releaseFromAnExistingTag = async (variables: { projectUID: string; fromReleaseTag: string }) => {
  return makeGraphQlCall(ReleaseFromAnExistingTagMutation, variables);
};
